import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";

export function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cr = entry.contentRect;
        setSize({ width: cr.width, height: cr.height });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, size] as const;
}

/** Live result of a CSS media query. */
export function useMediaQuery(query: string) {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === "undefined" || !window.matchMedia) return () => {};
      const mq = window.matchMedia(query);
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    },
    [query]
  );
  return useSyncExternalStore(
    subscribe,
    () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(query).matches,
    () => false
  );
}

/** True on touch-first devices (phones, tablets), where the canvas uses the
 *  hold-to-pick-up policy in `touchPickup.ts`. */
export function useCoarsePointer() {
  return useMediaQuery("(pointer: coarse)");
}

/** Touch-first viewports up to this width (tablets: iPad mini through iPad
 *  Pro 13" and Surface Pro 10 at 1440×960) get the phone layout. */
export const TABLET_MAX_WIDTH = 1440;

/**
 * Phone layout: narrow viewports, touch-first viewports too short for the
 * desktop chrome — landscape phones are 667–932px wide but only ~375–430px
 * tall — and touch-first tablets up to {@link TABLET_MAX_WIDTH}. Mirrored by
 * the `md` / `max-md` variants and the phone `:root` block in `index.css`.
 */
export const PHONE_QUERY =
  `(max-width: 767px), (pointer: coarse) and (max-height: 500px), (pointer: coarse) and (max-width: ${TABLET_MAX_WIDTH}px)`;
/** Landscape phones, which get the left mode rail and side sheets. */
export const PHONE_LANDSCAPE_QUERY =
  "(pointer: coarse) and (max-height: 500px) and (orientation: landscape)";

export type LayoutMode = "desktop" | "phone" | "phoneLandscape";

export function useLayoutMode(): LayoutMode {
  const phone = useMediaQuery(PHONE_QUERY);
  const landscape = useMediaQuery(PHONE_LANDSCAPE_QUERY);
  if (!phone) return "desktop";
  return landscape ? "phoneLandscape" : "phone";
}

/** True for the phone layout (portrait or landscape) — see {@link PHONE_QUERY}. */
export function useIsMobile() {
  return useMediaQuery(PHONE_QUERY);
}
